import { useEffect, useMemo } from 'react';
import useGoogleMap from '@rsos/components/capstone/common/Map/useGoogleMap';
import {
  BASEMAP_GOOGLE_HYBRID,
  BASEMAP_GOOGLE_ROADMAP,
} from '@rsos/constants/basemapAddonNames';
import useGoogleMapId from '@rsos/hooks/useGoogleMapId';
import { getGoogleMapsApiKey } from '@rsos/utils/metaTags';

const apiKey = getGoogleMapsApiKey();

/**
 * A component wrapping the Google Maps v3 API. Lazy loads the google maps
 * script if not already loaded. If it's already loaded, the component will
 * listen for the script load event before instantiating the map.
 * NOTE: All memory allocations from adding event listeners should be cleaned up
 * by the user of this component.
 * See: https://issuetracker.google.com/issues/35821412.
 * @param {String} id - DOM element id associated with Google Maps
 * @param {Function} onMapReady - Callback function when Google Maps is initialized
 * @param {Object} options - Google Maps options
 * @param {Object} styles - Google Maps styles
 */
const GoogleMap = ({
  id = '',
  onMapReady = () => {},
  options = {},
  style = {},
}) => {
  const mapId = useGoogleMapId();

  const mapOptions = useMemo(() => {
    return {
      ...options,
      fullscreenControl: false,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window?.google?.maps?.MapTypeControlStyle?.HORIZONTAL_BAR || 1,
        position: window?.google?.maps?.ControlPosition?.RIGHT_TOP || 7,
        mapTypeIds: [BASEMAP_GOOGLE_HYBRID, BASEMAP_GOOGLE_ROADMAP],
      },
      mapTypeId: BASEMAP_GOOGLE_ROADMAP,
      zoomControlOptions: {
        position: window?.google?.maps?.ControlPosition?.RIGHT_BOTTOM || 9,
      },
    };
  }, [options]);

  const { mapInstance, mapRef, initialized } = useGoogleMap({
    apiKey,
    mapId,
    options: mapOptions,
    styles: [],
  });

  // Callback for when Google Maps is initialized.
  useEffect(() => {
    if (initialized) {
      onMapReady(mapInstance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, mapInstance, mapRef]);

  return <div id={id} style={style} ref={mapRef} />;
};

export default GoogleMap;
